import { useCallback, useEffect, useState } from 'react';
import { CommunityReport, deleteAdminComment, deleteAdminPost, getAdminReports, updateAdminReportStatus } from '../../../api/adminApi';
import { cleanClaim } from '../../../utils/truthpickContent';
import './ReportManage.css';

type Filter = 'PENDING' | 'RESOLVED' | 'DISMISSED' | 'ALL';
type ReportStatus = 'PENDING' | 'RESOLVED' | 'DISMISSED';

const reasonLabel: Record<CommunityReport['reason'], string> = {
    SPAM: '광고·도배', ABUSE: '욕설·괴롭힘', FALSE_INFORMATION: '허위 정보', OTHER: '기타'
};

const ReportManage = ({
    loadReports = getAdminReports,
    updateStatus = updateAdminReportStatus
}: {
    loadReports?: (status: Filter) => Promise<CommunityReport[]>;
    updateStatus?: (id: number, status: ReportStatus) => Promise<void>;
}) => {
    const [filter, setFilter] = useState<Filter>('PENDING');
    const [reports, setReports] = useState<CommunityReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [error, setError] = useState('');

    const refresh = useCallback(async () => {
        setLoading(true); setError('');
        try { setReports(await loadReports(filter)); }
        catch { setError('신고 목록을 불러오지 못했습니다. 다시 시도해 주세요.'); }
        finally { setLoading(false); }
    }, [filter, loadReports]);

    useEffect(() => { void refresh(); }, [refresh]);

    const changeStatus = async (report: CommunityReport, status: ReportStatus) => {
        setBusyId(report.id); setError('');
        try { await updateStatus(report.id, status); await refresh(); }
        catch { setError('신고 상태를 저장하지 못했습니다.'); }
        finally { setBusyId(null); }
    };

    const removeContent = async (report: CommunityReport) => {
        if(!window.confirm(`${report.targetType === 'POST' ? '게시글' : '댓글'}을 삭제하고 신고를 처리 완료하시겠습니까?`)) return;
        setBusyId(report.id); setError('');
        try {
            if(report.targetType === 'POST') await deleteAdminPost(report.targetId);
            else await deleteAdminComment(report.targetId);
            await updateStatus(report.id, 'RESOLVED');
            await refresh();
        } catch { setError('콘텐츠 삭제 또는 신고 처리에 실패했습니다. 내용을 확인해 주세요.'); }
        finally { setBusyId(null); }
    };

    return <section className="report-manage">
        <header className="report-manage-head"><div><span>COMMUNITY / MODERATION</span><h1>신고 관리</h1><p>회원의 신고를 확인하고 게시글과 댓글을 검토하세요.</p></div><button type="button" onClick={refresh}>새로고침 ↻</button></header>
        <nav className="report-filters" aria-label="신고 상태">{([['PENDING','접수 대기'],['RESOLVED','처리 완료'],['DISMISSED','반려'],['ALL','전체']] as [Filter,string][]).map(([value,label]) => <button key={value} type="button" className={filter === value ? 'active' : ''} aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</button>)}</nav>
        {error && <div className="report-manage-error" role="alert">{error}</div>}
        {loading ? <div className="report-manage-empty">신고 내용을 불러오는 중...</div> : reports.length === 0 ? <div className="report-manage-empty"><strong>표시할 신고가 없습니다.</strong><p>새 신고가 접수되면 이곳에서 확인할 수 있습니다.</p></div> : <div className="report-list">{reports.map(report => <article className="report-item" key={report.id}>
            <div className="report-item-top"><div><span className={`report-pill ${report.status.toLowerCase()}`}>{report.status === 'PENDING' ? '접수 대기' : report.status === 'RESOLVED' ? '처리 완료' : '반려'}</span><span className="report-target-type">{report.targetType === 'POST' ? '게시글' : '댓글'} #{report.targetId}</span><span className="report-reason">{reasonLabel[report.reason] || report.reason}</span></div><time>{new Date(report.createdAt).toLocaleString('ko-KR')}</time></div>
            <h2>{report.targetContent ? cleanClaim(report.targetContent, 180) : '삭제된 콘텐츠'}</h2>
            {report.detail && <p className="report-detail">신고 내용 · {report.detail}</p>}
            <div className="report-item-foot"><span>신고자 {report.reporterEmail}</span><div>{report.status === 'PENDING' ? <><button type="button" disabled={busyId === report.id} onClick={() => changeStatus(report, 'RESOLVED')}>처리 완료</button><button type="button" disabled={busyId === report.id} onClick={() => changeStatus(report, 'DISMISSED')}>반려</button><button type="button" className="danger" disabled={busyId === report.id || !report.targetContent} onClick={() => removeContent(report)}>콘텐츠 삭제</button></> : <button type="button" disabled={busyId === report.id} onClick={() => changeStatus(report, 'PENDING')}>재검토</button>}</div></div>
        </article>)}</div>}
    </section>;
};

export default ReportManage;
